import { useEffect, useRef, useState } from 'react'

import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'

import {
  Card,
  CardHeader,
  CardTitle,
  cardViewAllLinkStyles,
} from '@/buyer-portal/components/layouts/CardLayout/card/ui'
import LegacyBpLink from '@/buyer-portal/components/LegacyBpLink'

import { Grid } from '../ListingGrid/ListingGrid'
import type { MoreFromSellerShellProps } from './MoreFromSellerShellProps'

const ViewAll = styled(LegacyBpLink)`
  ${cardViewAllLinkStyles}
`

// Clip the (reused) ALP grid to a single row: define one explicit row, collapse
// any wrapped rows to zero height, and hide the overflow. Cards that wrap past
// row 1 stop being visible; the column count stays fluid per available width.
const Body = styled.div`
  overflow: hidden;
  padding: 1rem;

  ${Grid} {
    gap: 1rem;
    grid-auto-rows: 0;
    grid-template-rows: max-content;
    overflow: hidden;
    row-gap: 0;
  }
`

/**
 * V2 (LDP redesign) presentation for the "More from this seller" module: a
 * grey-bg-compatible card with an uppercase header naming the seller and a
 * single fluid row of listing cards. Data, providers, tracking and the grid of
 * cards are supplied as `children`.
 */
export const MoreFromSellerCard = ({
  children,
  containerRef,
  isLoading,
  listingCount,
  onBrowseAllClick,
  storefront,
  storefrontUrl,
  total,
}: MoreFromSellerShellProps) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(listingCount)

  // Count how many cards land in the first (only visible) row so the "View all"
  // link can appear when fetched cards wrap out of view, not just when more
  // results exist server-side.
  useEffect(() => {
    const body = bodyRef.current
    if (!body || typeof ResizeObserver === 'undefined') {
      setVisibleCount(listingCount)
      return
    }
    const measure = () => {
      const grid = body.querySelector<HTMLElement>(
        '[data-more-from-seller-grid]'
      )
      const cards = grid ? (Array.from(grid.children) as HTMLElement[]) : []
      const [firstCard] = cards
      if (!firstCard) {
        setVisibleCount(0)
        return
      }
      const firstTop = firstCard.offsetTop
      setVisibleCount(
        cards.filter((card) => card.offsetTop === firstTop).length
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(body)
    return () => observer.disconnect()
  }, [listingCount])

  return (
    <Card ref={containerRef}>
      <CardHeader>
        <CardTitle>
          {storefront.name ? (
            <FormattedMessage
              id="Auction.title.moreFromSellerName"
              values={{ sellerName: storefront.name }}
            />
          ) : (
            <FormattedMessage id="Auction.title.moreFromThisSeller" />
          )}
        </CardTitle>
        {storefrontUrl && (isLoading || total - 1 > visibleCount) ? (
          <ViewAll href={storefrontUrl} onClick={onBrowseAllClick}>
            <FormattedMessage id="Auction.details.carousel.viewAll" />
            <FontAwesomeIcon icon={faArrowRight} size="xs" />
          </ViewAll>
        ) : null}
      </CardHeader>
      <Body ref={bodyRef}>{children}</Body>
    </Card>
  )
}
